import type { FolderImportIdentity, StagedCapture } from '../shared/desktop-api';
import type { WorkingReadout } from './working-progress';

/**
 * ONE FOLDER, ONE COUNT.
 *
 * Owner report 2026-09-27: a folder of 100 files, added to an existing job,
 * showed "40 documents". The folder is staged as waves (a small first wave so
 * review opens fast, then larger ones), and every count on screen was read off
 * the wave being worked, so the operator saw a wave size and concluded the
 * product had dropped most of the folder. Waves are scheduling only. From the
 * moment a folder is added every count refers to the whole folder: "Adding 100
 * files", "42 of 100 read", counting up across waves and never down.
 *
 * The totals are the staged captures' own file counts. A file counts as read
 * as soon as every one of its pages is read (owner, October 5, 2026: a 100-file
 * folder sat at "0 of 100 files read" for minutes while the reader was visibly
 * finding tables and barcodes, because the folder's pages are read ahead and the
 * count moved only when a whole wave committed). The main process counts those
 * files exactly from the engine's own page events, per wave (`filesRead`).
 *
 * A wave's last file is held back until its values are in (owner, October 4,
 * 2026: a 10-file setup said "All 10 files read" for 55 s while two line-item
 * tables were still being read, and looked stuck), so the folder never says
 * "100 of 100" while it still works. A wave pulling values also counts the
 * documents it has finished where they line up with files: pages done when each
 * file is one page, documents done when each file is one document. Saving,
 * finished, or gone from the queue, all its files are read. Nothing is
 * estimated. A wave whose folder cannot be known says "Adding your folder…"
 * with no count.
 */

type Found = { pages: number; documents: number; barcodes: number; values: number; rows: number };
type Tracked = {
  waves: FolderImportIdentity['waves'];
  discarded: Set<string>;
  read: number;
  startedAt: number;
  found: Map<string, Found>;
  /** Files of each wave whose every page is read, as the main process counted them; only ever raised (a discarded wave's entry is dropped). */
  pagesRead: Map<string, number>;
};
/** Folder imports seen by this workspace, kept across waves (a finished wave leaves the queue). */
export type FolderImportRegistry = Map<string, Tracked>;
/** The app's folder imports: a workspace that remounts mid-import keeps counting the same folder (never "42 of 100" after "100 of 100"). */
export const appFolderImports: FolderImportRegistry = new Map();

export type WaveProgress = { stage: string; completed: number; total: number } & Partial<{
  pageCount: number;
  documentCount: number;
  barcodeCount: number;
  fieldCount: number;
  lineItemCount: number;
  /** Pages whose document has finished the current stage. */
  completedPageCount: number;
  /** Files of each wave of this folder whose every page is read, by wave id. */
  filesRead: Record<string, number>;
}>;

export type FolderImportProgress = {
  id: string;
  /** Files in the folder; null when the folder's size cannot be known. */
  total: number | null;
  /** Files read so far, never decreasing. */
  read: number;
  found: Found;
  /** Time left from the import's own pace, once three files are read; null otherwise. */
  remainingMilliseconds: number | null;
};

const waveLabel = /\s·\s*wave \d+ of \d+\s*$/iu;

/** A capture's name for the operator: a folder wave is named by its folder alone. */
export const captureDisplayName = (item: Pick<StagedCapture, 'sourceLabel'>) =>
  item.sourceLabel.replace(waveLabel, '');

/** A wave of a folder, whether or not its folder is known here (legacy manifests). */
export const isFolderWave = (item: Pick<StagedCapture, 'sourceLabel' | 'folderImport'>) =>
  Boolean(item.folderImport) || waveLabel.test(item.sourceLabel);

/**
 * The staging result as the queue should hold it: every wave carries the folder
 * it belongs to, even from a main process that does not say so yet.
 */
export function stampFolderImport(captures: readonly StagedCapture[]): StagedCapture[] {
  if (captures.length === 0 || captures.every((item) => item.folderImport)) return [...captures];
  const folderImport: FolderImportIdentity = {
    id: captures[0].id,
    waves: captures.map((item) => ({ id: item.id, fileCount: item.fileCount })),
  };
  return captures.map((item) => (item.folderImport ? item : { ...item, folderImport }));
}

/** Remember every folder the queue mentions, so its finished waves still count after they leave. */
export function registerFolderImports(
  registry: FolderImportRegistry,
  queue: readonly StagedCapture[],
  now = Date.now(),
) {
  for (const item of queue) {
    const identity = item.folderImport;
    if (!identity) continue;
    const known = registry.get(identity.id);
    if (!known) {
      registry.set(identity.id, {
        waves: [...identity.waves],
        discarded: new Set(),
        read: 0,
        startedAt: now,
        found: new Map(),
        pagesRead: new Map(),
      });
      continue;
    }
    for (const wave of identity.waves)
      if (!known.waves.some((existing) => existing.id === wave.id)) known.waves.push(wave);
  }
}

/**
 * A discarded wave's files were never read; they leave the folder's total rather than count as done.
 * Its pages may already have been read ahead and counted: the count drops them and is rebuilt from
 * the waves still in the folder, or it would say "10 of 10 read" while files are still unread.
 */
export function discardFolderImportWave(registry: FolderImportRegistry, captureId: string) {
  for (const tracked of registry.values())
    if (tracked.waves.some((wave) => wave.id === captureId)) {
      tracked.discarded.add(captureId);
      tracked.pagesRead.delete(captureId);
      tracked.found.delete(captureId);
      tracked.read = 0;
    }
}

/** Stage order as the working screen ranks it; "reading" is the benchmark name for recognition. */
const stageRank = (stage: string) =>
  stage === 'complete'
    ? 5
    : stage === 'reading'
      ? 1
      : ['preparing', 'recognizing', 'organizing', 'understanding', 'finalizing'].indexOf(stage);

const n = (value: number | undefined) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

const waveRead = (fileCount: number, progress: WaveProgress | undefined, pagesRead: number) => {
  // Files whose pages are all read, short of the wave's last until its values are in.
  const paged = Math.min(pagesRead, fileCount - 1);
  if (!progress) return paged;
  const rank = stageRank(progress.stage);
  if (rank >= 4) return fileCount; // every document's values are in: saving, or complete
  if (rank !== 3) return paged; // documents found are not documents done
  const done = Math.max(
    progress.pageCount === fileCount ? n(progress.completedPageCount) : 0,
    progress.total === fileCount ? n(progress.completed) : 0,
  );
  return Math.max(paged, Math.min(fileCount, done));
};

const add = (sum: Found, item: Found): Found => ({
  pages: sum.pages + item.pages,
  documents: sum.documents + item.documents,
  barcodes: sum.barcodes + item.barcodes,
  values: sum.values + item.values,
  rows: sum.rows + item.rows,
});
const none: Found = { pages: 0, documents: 0, barcodes: 0, values: 0, rows: 0 };

/**
 * The folder being added right now, counted whole. Null when no folder wave is
 * waiting or being read (a failed wave on its own is the retry card's to say).
 * Folds the waves' progress into the registry, so it is idempotent per render.
 */
export function folderImportProgress(
  registry: FolderImportRegistry,
  queue: readonly StagedCapture[],
  waves: ReadonlyMap<string, WaveProgress>,
  now = Date.now(),
): FolderImportProgress | null {
  const working = queue.find((item) => item.status === 'pending' && isFolderWave(item));
  if (!working) return null;
  const tracked = working.folderImport ? registry.get(working.folderImport.id) : undefined;
  if (!tracked)
    return { id: working.id, total: null, read: 0, found: none, remainingMilliseconds: null };
  const queued = new Map(queue.map((item) => [item.id, item]));
  // A wave's pages may be read ahead under another wave's id: every event names the waves it counted.
  for (const progress of waves.values())
    for (const [id, files] of Object.entries(progress.filesRead ?? {}))
      if (!tracked.discarded.has(id) && tracked.waves.some((wave) => wave.id === id))
        tracked.pagesRead.set(id, Math.max(tracked.pagesRead.get(id) ?? 0, n(files)));
  let total = 0;
  let read = 0;
  for (const wave of tracked.waves) {
    if (tracked.discarded.has(wave.id)) continue;
    total += wave.fileCount;
    const progress = waves.get(wave.id);
    if (progress) {
      const prior = tracked.found.get(wave.id) ?? none;
      tracked.found.set(wave.id, {
        pages: Math.max(prior.pages, n(progress.pageCount)),
        documents: Math.max(prior.documents, n(progress.documentCount)),
        barcodes: Math.max(prior.barcodes, n(progress.barcodeCount)),
        values: Math.max(prior.values, n(progress.fieldCount)),
        rows: Math.max(prior.rows, n(progress.lineItemCount)),
      });
    }
    const item = queued.get(wave.id);
    // Gone from the queue and not discarded: the wave finished and its documents are in the batch.
    read += !item
      ? wave.fileCount
      : item.status === 'failed'
        ? 0
        : waveRead(wave.fileCount, progress, tracked.pagesRead.get(wave.id) ?? 0);
  }
  tracked.read = Math.min(total, Math.max(tracked.read, read));
  const found = [...tracked.found.entries()]
    .filter(([id]) => !tracked.discarded.has(id))
    .map(([, value]) => value)
    .reduce(add, none);
  const elapsed = now - tracked.startedAt;
  const remainingMilliseconds =
    tracked.read >= 3 && tracked.read < total && elapsed > 0
      ? (elapsed / tracked.read) * (total - tracked.read)
      : null;
  return { id: working.folderImport!.id, total, read: tracked.read, found, remainingMilliseconds };
}

const files = (value: number) =>
  `${value.toLocaleString('en-US')} ${value === 1 ? 'file' : 'files'}`;

/** The two lines every folder surface says: what is being added, and how far along. */
export function folderImportLines(progress: FolderImportProgress): {
  title: string;
  count: string | null;
} {
  if (progress.total === null) return { title: 'Adding your folder…', count: null };
  return {
    title: `Adding ${files(progress.total)}`,
    count: `${progress.read.toLocaleString('en-US')} of ${progress.total.toLocaleString('en-US')} read`,
  };
}

/**
 * The status bar's line while a folder is still being read behind the documents on screen
 * (owner, October 3, 2026, reversing the September 23 rule that the bar never shows import
 * progress): what is left, and how far along. Files, because files are what is counted;
 * how many documents they hold is known only once each is read.
 */
export function folderImportStatus(progress: FolderImportProgress): {
  text: string;
  count: string | null;
  fraction: number | null;
} {
  if (progress.total === null) return { text: 'Adding your folder…', count: null, fraction: null };
  const left = Math.max(0, progress.total - progress.read);
  return {
    text:
      left > 0
        ? `Reading ${left.toLocaleString('en-US')} more ${left === 1 ? 'file' : 'files'}`
        : 'Finishing your files',
    count: `${progress.read.toLocaleString('en-US')} of ${progress.total.toLocaleString('en-US')}`,
    fraction: progress.total > 0 ? Math.min(1, progress.read / progress.total) : 0,
  };
}

const clock = (milliseconds: number) => {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60)
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * The working screen's readout, recounted for the whole folder: the hero is
 * files read of the folder, the facts are the folder's totals, and time left is
 * the folder's own pace. The stage list and the live line still narrate the
 * work in hand; neither carries a number of its own.
 */
export function folderImportReadout(
  readout: WorkingReadout,
  progress: FolderImportProgress,
): WorkingReadout {
  const details = [
    { label: 'Pages', value: progress.found.pages.toLocaleString() },
    { label: 'Barcodes', value: progress.found.barcodes.toLocaleString() },
    { label: 'Documents', value: progress.found.documents.toLocaleString() },
    ...readout.details.filter((item) => item.label === 'Types'),
    { label: 'Values read', value: progress.found.values.toLocaleString() },
    { label: 'Line items', value: progress.found.rows.toLocaleString() },
  ];
  const metrics = readout.metrics.map((item) =>
    item.label === 'Left'
      ? {
          label: 'Left',
          value: progress.remainingMilliseconds
            ? `~${clock(progress.remainingMilliseconds)}`
            : 'Estimating…',
        }
      : item,
  );
  if (progress.total === null)
    return {
      ...readout,
      headline: '–',
      detail: '',
      completed: undefined,
      total: undefined,
      bar: 0,
      indeterminate: true,
      valueText: `Adding your folder. ${readout.live.text}`,
      metrics,
      details,
      remainingMilliseconds: null,
    };
  const headline = `${progress.read.toLocaleString()} of ${progress.total.toLocaleString()}`;
  return {
    ...readout,
    headline,
    detail: 'files read',
    completed: progress.read,
    total: progress.total,
    bar: progress.total > 0 ? Math.min(1, progress.read / progress.total) : 0,
    indeterminate: false,
    valueText: `${headline} files read. ${readout.live.text}`,
    metrics,
    details,
    remainingMilliseconds: progress.remainingMilliseconds,
  };
}

/** The import each capture belongs to, kept for the life of the app (a finished wave leaves the queue). */
const captureImports = new Map<string, string>();

/**
 * The import a capture is part of: its folder import's id for a folder wave,
 * otherwise the capture itself. The working screen keys its one progress run by
 * this, so the waves of one folder continue one set of steps and counts, and a
 * new import starts its own.
 */
export function rememberCaptureImport(
  queue: readonly Pick<StagedCapture, 'id' | 'folderImport'>[],
  captureId: string,
) {
  for (const item of queue)
    if (item.folderImport && !captureImports.has(item.id))
      captureImports.set(item.id, item.folderImport.id);
  return captureId ? (captureImports.get(captureId) ?? captureId) : '';
}
